import { fetchForecastResult } from "../../services/forecastRowApi.js";
import { fmtPct, sideChanceText } from "./formatPct.js";
import { PENDING_FORECAST_KEY } from "./storageKeys.js";
import { parseJsonBodyOrNull } from "../apiClient.js";
import { filterAndStampEvents, eventEndMs } from "./model.js";
import { stampOccurrenceKeys } from "./eventIdentity.js";
import { effectiveTriggerModel, toServableEngine } from "./engines.js";
import { recordUiEvent } from "../logRingBuffer.js";
import { callLlm } from "./llmClient.js";
import { withBackground } from "./contractBackground.js";
import {
  boundedResultInspection,
  isRecoveryWaitExpiredError,
  isUnansweredRecoveryError,
  FORECAST_RECOVERY_WINDOW_MS,
  resolveRecoveryDeadlineAt,
} from "../forecastStream.js";

// Random unique id with a graceful fallback. crypto.randomUUID() is unavailable
// in non-secure contexts (http://) and older browsers, where calling it throws —
// so feature-detect and fall back to a timestamp+random id.
const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

// The reload handle is a single shared slot, so an older run can settle after a
// newer submit has replaced it. Only the run that wrote the current trace/client
// pair may clear it; otherwise the newer run would lose its reload recovery path.
export const clearPendingForecastIfOwned = (traceId, clientId) => {
  if (!traceId || !clientId) return;
  try {
    const raw = sessionStorage.getItem(PENDING_FORECAST_KEY);
    if (!raw) return;
    const pending = JSON.parse(raw);
    if (pending?.traceId === traceId && pending?.clientId === clientId) {
      sessionStorage.removeItem(PENDING_FORECAST_KEY);
    }
  } catch (_e) {
    /* private mode / parse failure — leave the handle alone */
  }
};

// stripDebugForWire moved to ./contentSave.ts — it is now typed against the
// client↔Express wire contract (app/src/types/persistence.ts) so that a
// PATCH-only field on the content-save blob is a COMPILE error, not a runtime
// 400 (the #1904 regression). Re-exported here so existing importers
// (useForecastPersistence, forecastClient.test.js) keep their import path.
export { stripDebugForWire } from "./contentSave.js";

// A resume/reattach SSE that is interrupted by the page navigating away (reload)
// rejects with an abort/network error, NOT a genuine forecast failure. Such a
// drop is transient — the run is still live server-side and the next load's
// resume sweep reconnects — so callers show a "Reconnecting…" status and keep the
// run resumable instead of persisting a sticky error or flipping it to errored.
export function isAbortLikeResumeError(e) {
  return (
    e?.name === "AbortError" ||
    /failed to fetch|network ?error|load failed|aborted|the user aborted/i.test(
      e?.message || String(e || ""),
    )
  );
}

// Build the contract-panel shape from a preprocessed question payload
// ({question_text, title, scheduled_resolution_time, resolution_criteria}).
// Shared by submit() (earlyContract), the edit-resubmit panel, and the resume
// path (reattachToForecast) so the contract a resumed run shows matches what
// the original submit showed — closing the bug where a reloaded/log-clicked
// forecast displayed only the bare question text in the contract panel until
// the run finished. fallbackResolutionDate (a placeholder year-out date) is
// used when the preprocess result carried no scheduled_resolution_time.
/** The calendar day of `d` as seen in `zone` — "YYYY-MM-DD".
 *
 * Not `toISOString().slice(0,10)`, which is UTC's day and differs from the
 * viewer's for a large part of every 24 hours. Falls back to UTC when the zone
 * is absent or unloadable, which is the behaviour this replaces.
 */
export function dayInZone(d, zone) {
  if (!zone) return d.toISOString().slice(0, 10);
  try {
    // en-CA renders ISO-ordered YYYY-MM-DD, so no reassembly is needed.
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: zone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/** The deadline instant from a preprocess payload, or null.
 *
 * The ONE place that knows how to read it. `scheduled_resolution_time` is
 * naive-UTC on the wire ("2026-09-16T20:00:00", no offset), and JavaScript
 * parses a full timestamp without one as LOCAL — a Tokyo browser turns a 20:00Z
 * deadline into 11:00Z. `custom_metadata.resolution_moment_utc` carries the same
 * instant with an explicit Z.
 *
 * Centralised because the naive field had TWO readers: fixing this one left
 * `editResubmit`'s past-date guard shifting by the viewer's offset, rejecting a
 * future edit in a positive zone and accepting a past one in a negative zone. A
 * new consumer should call this rather than reach for either field.
 *
 * Falls back to the naive value for a server that does not send the explicit one
 * — the previous behaviour, not a new failure mode.
 */
export function deadlineOf(pp) {
  const p = pp && typeof pp === "object" ? pp : {};
  const raw = p.custom_metadata?.resolution_moment_utc || p.scheduled_resolution_time;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * The deadline ONLY when the server resolved a real instant for it, else null.
 *
 * Deliberately NOT `deadlineOf`, which falls back to `scheduled_resolution_time`.
 * That fallback is right for reading a deadline and wrong for deciding whether
 * one has PASSED: a date-only deadline is stored as its own midnight, so
 * comparing it as an instant would call every same-day forecast past from the
 * moment the day began. Callers that need "is this actually behind us" ask for
 * the exact instant and fall back to the day-based rule when there is none.
 */
export function exactDeadlineOf(pp) {
  const raw = pp && typeof pp === "object" ? pp.custom_metadata?.resolution_moment_utc : null;
  if (!raw) return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

export function contractFromPreprocessed(pp, rawQ, fallbackResolutionDate) {
  // Always returns a valid contract object (never null) so callers can read
  // .resolution_date / spread it without a guard. A missing or non-object `pp`
  // degrades to a bare contract built from rawQ + the fallback date.
  const q = rawQ || "";
  const p = pp && typeof pp === "object" ? pp : {};
  // Guard the date parse: a corrupted sessionStorage handle or a changed
  // preprocess payload shape could carry an invalid scheduled_resolution_time,
  // and `new Date(bad).toISOString()` throws a RangeError — which would break
  // resume/reattach rendering. Fall back to the placeholder date when invalid.
  const sd = deadlineOf(p);
  // The instant the server RESOLVED, which is null for a date-only deadline —
  // unlike `sd`, which falls back to `scheduled_resolution_time` so that a legacy
  // payload still renders. That difference is load-bearing below.
  const exact = exactDeadlineOf(p);
  // The zone the server resolved this deadline in, and WHICH rule it used. Both
  // ride custom_metadata like `assertion`; absent on an older server, which
  // degrades to the previous UTC behaviour rather than breaking.
  const resolution_zone = p.custom_metadata?.resolution_zone || "";
  const resolution_basis = p.custom_metadata?.resolution_basis || "";
  // The DISPLAYED day is the day the deadline falls on in its own zone: a New York
  // end-of-day on 1 Oct is 2026-10-02T03:59:59Z, and formatting that in UTC would
  // show the user 2 October for a deadline they set on the 1st.
  //
  // The zone is applied to the MOMENT, never to `sd`. For a date-only deadline `sd`
  // is a midnight, and midnight UTC in New York is the PREVIOUS day — so using it
  // here would re-key exactly the contracts this is meant to keep stable. Same rule
  // as the proxy's `_resolutionDayOf`, deliberately.
  const resolution_date = exact
    ? dayInZone(exact, resolution_zone)
    : sd
      ? sd.toISOString().slice(0, 10)
      : fallbackResolutionDate;
  return {
    title: p.title || p.question_text || q,
    question: p.question_text || q,
    // Present-tense declarative form of the question, when the contract server produced one
    // (it rides in custom_metadata). Surfaces that STATE the claim rather than ask it use
    // this; absent — an older server, or a question it declined to rephrase — they fall back
    // to the question, which is the previous behaviour.
    assertion: p.custom_metadata?.assertion || "",
    resolution_date,
    // The EXACT deadline the server resolved, kept beside the day.
    //
    // `resolution_date` is the identity value — it feeds the contract hash — so
    // it stays day-granular. Without these three the instant existed only for as
    // long as the preprocess payload did: a reforecast or a branch rebuilds its
    // request from the CONTRACT, so a 20:00Z market close came back as its own
    // midnight on the first rerun. They also let a surface show the exact moment
    // and say why it was chosen — a guess nobody can see is a guess nobody can
    // correct.
    //
    // All three together, and only when an exact moment was actually resolved.
    // Deriving the moment from `sd` fabricated one for a legacy date-only payload,
    // which `buildReforecastPreprocessed` then sent back as `resolution_moment_utc`
    // — turning a midnight into a claimed instant and re-keying the contract.
    resolution_moment: exact ? exact.toISOString() : "",
    resolution_zone: exact ? resolution_zone : "",
    resolution_basis: exact ? resolution_basis : "",
    // Display only (not in the contract hash): plain words for the moment.
    resolution_label: exact ? p.custom_metadata?.resolution_label || "" : "",
    resolution_criteria: p.resolution_criteria || "",
    background: "",
    sources: [],
    probability: null,
  };
}

/** The premise a branch's run wrote (a typed premise's key and wording), from its result. */
function premiseOfResult(data) {
  const key = typeof data?.premise_key === "string" && data.premise_key ? data.premise_key : null;
  const contract =
    key && data.premise_contract && typeof data.premise_contract === "object"
      ? data.premise_contract
      : null;
  return { premiseKey: key, premiseContract: contract };
}

export async function genForecastFromServer(
  q,
  engine,
  assumption = null,
  debug = false,
  preprocessedQuestion = null,
  signal = null,
  opts = {},
) {
  // Onboarding tour: return baked fixture data, no network. A truthy assumption
  // means this is the branch rerun (beat 4), so return the branch variant. Deep
  // clone and reshape into the SAME object genForecastFromServer normally
  // returns (top-level question is the STRING q, not the preprocess object —
  // otherwise it renders as a React child and crashes), so the downstream
  // doForecast/handleBranch render path is identical to a live forecast.
  const tourFixture = opts.tourFixture;
  if (tourFixture) {
    // Capture the fixture up front: if the user skips the tour mid-generation,
    // endTour nulls __tourFixture while this is still awaiting — using the
    // captured `fx` avoids a null deref after the hold.
    const fx = tourFixture;
    // The fixture is instant, but the tour wants the FIRST (base) forecast to
    // look like it's genuinely generating — hold the "Generating Forecast"
    // placeholder ~5s AND emit simulated progress events so the chat shows the
    // same stage-label ticks ("Researching sources, N% done") a real forecast
    // does (the fixture path otherwise never calls onProgress). Branch reruns
    // (assumption set) stay snappy.
    if (!assumption && !signal?.aborted) {
      const tourProg = typeof opts.onProgress === "function" ? opts.onProgress : null;
      const fire = (stage, progress) => {
        if (tourProg && !signal?.aborted) {
          try {
            tourProg({ stage, progress });
          } catch (_e) {
            /* UI hiccup shouldn't drop the demo */
          }
        }
      };
      const beat = (ms) => new Promise((r) => setTimeout(r, ms));
      fire("preprocess");
      await beat(700);
      // Climb the research % smoothly over ~3.6s, mirroring the real agent loop.
      for (const p of [12, 28, 44, 61, 78, 92]) {
        if (signal?.aborted) break;
        fire("researching", p);
        await beat(600);
      }
      fire("postprocess");
      await beat(700);
    }
    let src;
    if (!assumption) src = fx.base;
    else if (opts.tourBranchIncrease)
      src = fx.branch; // raises the forecast
    else {
      // Lowers the forecast (e.g. a red trigger occurring). Synthesize a
      // down-branch from the base with a clearly-lower probability so the tour
      // reflects direction instead of always showing the baked increase.
      src = JSON.parse(JSON.stringify(fx.base));
      const bp = fx.base.contract?.probability ?? 0.18;
      const np = Math.max(0.02, +(bp * 0.4).toFixed(2));
      src.contract = { ...src.contract, probability: np };
      // The base report/explanation describe the BASE forecast — opening this
      // branch's report would show reasoning contradicting the lowered
      // probability. Drop the structured report (branches without one render
      // the explanation text, same as the non-agent engines) and swap in a
      // short direction-consistent explanation.
      src.report = null;
      src.explanation = `If this trigger happens, the chance falls to about ${fmtPct(np)}%.`;
    }
    const f = JSON.parse(JSON.stringify(src));
    const now = new Date();
    const events = (f.events || []).map((e) => {
      // Same local-day rule as filterAndStampEvents (see eventEndMs): a date-only
      // event is past only once its LOCAL day ends, not from UTC midnight.
      const dMs = eventEndMs(e.date);
      const endMs = e.date_end ? eventEndMs(e.date_end) : dMs;
      e.isPast = (isNaN(endMs) ? dMs : endMs) <= now.getTime();
      return e;
    });
    return {
      id: "fc-" + uid(),
      question: q,
      createdAt: new Date().toISOString(),
      branches: [],
      serverPrediction: typeof f.contract?.probability === "number" ? f.contract.probability : 0,
      serverExplanation: f.explanation || "",
      // The fixture is a recorded result wire, which says `contract.context`.
      contract: withBackground(f.contract),
      events,
      report: f.report || null,
      serverContext: {
        explanation: f.explanation || "",
        metadata: {},
        question: {},
        report: f.report || null,
      },
      _debug: null,
      _obs: null,
    };
  }
  // opts.onTraceAllocated({traceId, forecastId}) is invoked once when the
  // SSE proxy's first response header arrives. Callers use it to populate
  // React state (e.g. activeTraceId) so the Cancel button can target the
  // running forecast. Same hook captures the chatboard forecasts.id (UUID)
  // used by the messages-persistence API.
  const onTraceAllocated =
    typeof opts.onTraceAllocated === "function" ? opts.onTraceAllocated : null;
  // opts.onProgress({stage, step, progress, data}) fires for each SSE
  // `progress` event. Callers update a UI label (e.g. "Researching sources,
  // 33% done") so the user sees the forecast is alive during the long
  // research phase. heartbeat events are intentionally ignored — they keep
  // the connection warm but carry no UI signal.
  const onProgress = typeof opts.onProgress === "function" ? opts.onProgress : null;
  // opts.onUpdate(frame) fires for each SSE `update` frame — one raw
  // ForecastProgressUpdate. Absent on producers that emit only progress/done.
  const onUpdate = typeof opts.onUpdate === "function" ? opts.onUpdate : null;
  // opts.onNarration({text, ordinal, phase}) fires for each SSE `narration`
  // frame — one beat of plain-language commentary about what the research
  // agents are doing, minted by the relay rather than the forecaster. It is a
  // separate wire from `update` on purpose: narration is not part of the
  // forecast document and carries no IDL `seq`, so it never moves the resume
  // cursor. Absent on producers that do not narrate.
  const onNarration = typeof opts.onNarration === "function" ? opts.onNarration : null;
  // opts.onContractKey({key, key_status}) fires when the forecast SSE stream carries
  // the server-allocated contract key (User#<hash>) — emitted by the proxy right after
  // it stamps the key, before the upstream forecast opens. Callers record it into the
  // client log ring buffer (ambient context + a discrete ui.forecast.contract_key
  // event). Optional; null when not provided.
  const onContractKey = typeof opts.onContractKey === "function" ? opts.onContractKey : null;
  // opts.clientId is the local pendingId — passing it to the proxy makes the
  // server-side lifecycle row use the same wire-side id as the optimistic
  // placeholder, so after reload the row from /api/forecasts dedups against
  // the resume-effect placeholder instead of appearing as a duplicate entry.
  const clientId = typeof opts.clientId === "string" ? opts.clientId : null;
  // opts.pairLeg=true marks this as one leg of a compare PAIR. Both legs carry a
  // clientId (so their lifecycle rows dedup), but they must NOT each write the
  // single-slot PENDING_FORECAST_KEY — the second would clobber the first and only
  // one root would auto-reconnect on reload. The run-pair caller persists a
  // dedicated dual-leg handle (comparePending) from its onTraceAllocated instead,
  // so we skip the single-key write here when pairLeg is set. Single-forecast
  // submits never pass pairLeg, so their resume handle is written exactly as before.
  const pairLeg = opts.pairLeg === true;
  // opts.postprocessModel: per-leg trigger-LLM override (bare OpenRouter id or
  // null). Sent as the `postprocess_model` wire field; the server prepends
  // openrouter/ and validates. Null/absent → server default trigger LLM.
  const postprocessModel = effectiveTriggerModel(opts.postprocessModel);
  // Branch's immediate parent server UUID. Required when assumption is set
  // (server validates user owns the parent row before INSERT).
  const parentForecastId = typeof opts.parentForecastId === "string" ? opts.parentForecastId : null;
  // Normalize a possibly-retired stored engine (an old forecast's `backend`,
  // inherited by a branch/reforecast) to a servable one before sending — else
  // the backend 400s "unknown orchestrator". Reassigned so the remap also flows
  // into the run's stored `backend`/`metadata.orchestrator` below; no-op for an
  // already-servable engine.
  const requestedEngine = engine;
  engine = toServableEngine(engine);
  if (engine !== requestedEngine) {
    // Bug-report breadcrumb: a retired/stale engine was silently re-pointed to a
    // live one. Captured in the client log ring buffer so a triager sees the
    // substitution behind a "why did my branch run on a different engine" report
    // (and, before this fix landed, the HTTP 400 it prevents). isBranch tells a
    // parent-inherited remap apart from a top-level one.
    recordUiEvent({
      event: "forecast.engine.remapped",
      from: requestedEngine,
      to: engine,
      isBranch: typeof opts.parentForecastId === "string" || assumption != null,
    });
  }
  const orchestrator = engine;
  // Multi-value ("multiway") mode: the multi-preprocess contract carries an
  // `outcome_space`. When present, forecast this as a distribution on the SAME
  // forecaster the user chose — the client no longer routes MW requests to a special
  // lane; the forecast server refuses (422), before any spend, a lane that cannot
  // answer with a distribution — and lift `outcome_space` to a TOP-LEVEL sibling of
  // `question` (the forecast server reads it there, never nested inside `question`).
  // A binary contract has no `outcome_space`, so the body is unchanged.
  // MW mode source is EITHER the preprocessed contract's outcome_space (top-level submit)
  // OR opts.outcomeSpace (the branch reforecast, which streams a plain-string question and
  // has no preprocessed object).
  const outcomeSpace =
    (preprocessedQuestion && preprocessedQuestion.outcome_space) || opts.outcomeSpace || null;
  const body = {
    orchestrator,
    question: preprocessedQuestion || {
      question_text: q,
      question_type: "binary",
      id_of_question: Date.now(),
    },
  };
  if (outcomeSpace) body.outcome_space = outcomeSpace;
  if (clientId) body.client_id = clientId;
  if (opts.forecastId) body.forecast_id = opts.forecastId;
  if (preprocessedQuestion) body.preprocessed = true;
  if (assumption) body.assumption = assumption;
  if (debug) body.debug = true;
  if (postprocessModel) body.postprocess_model = postprocessModel;
  // Assumption-injected calls are branches — counterfactual reruns of an
  // existing forecast. Branches get their own lifecycle row keyed off
  // parent_forecast_id (the immediate parent's server UUID). The proxy
  // validates the user owns that parent before INSERT. CASCADE handles
  // cleanup on root delete.
  if (assumption) body.branch = true;
  if (assumption && parentForecastId) body.parent_forecast_id = parentForecastId;
  // Thread fork metadata so the server can persist a non-null fork column on
  // branch-create. Only written when present (null/undefined skipped) to keep
  // the server's forkPayload null-when-empty guard meaningful.
  if (assumption) {
    if (opts.forkDate != null) body.forkDate = opts.forkDate;
    if (opts.modification != null) body.modification = opts.modification;
    if (opts.forkEvent != null) body.forkEvent = opts.forkEvent;
    // Parent's prior for the branch re-forecast (headline p0 + report reasons +
    // the clicked trigger's a/b) — the server injects it as a prior to revise.
    if (opts.branchContext != null) body.branch_context = opts.branchContext;
    // Explicit branch sense (occurs/not_occurs/reschedule/custom) so the server
    // uses it directly instead of sniffing the assumption text. Omitted (null) for
    // reforecast reruns → the server falls back to sniffing the carried assumption.
    if (opts.branchSense != null) body.branch_sense = opts.branchSense;
  }
  let data;
  // Postprocess sometimes flakes and returns just a probability with no contract
  // or no events. `isIncomplete` sets the final `_partial` flag.
  const isIncomplete = (d) => {
    if (!d || !d.contract) return true;
    // A multi-value ("multiway") forecast's answer IS the distribution (forecast_value),
    // not the triggers — so it is never "incomplete" for having no/empty events. (It does
    // normally carry trigger events now, but this stays correct if trigger-gen degrades
    // to none.)
    if (d.forecast_value || d.contract.forecast_value) return false;
    return !Array.isArray(d.events) || d.events.length === 0;
  };
  // Captured from the streaming `contract_key` SSE event (below); declared at
  // function scope so the _obs stamp after the stream can read them.
  let contractKey = null,
    contractKeyStatus = null;
  // POST /api/forecasts/run (the SSE streaming proxy over the GCS-persisted
  // result; sooth-cloud-design/docs/chatboard/forecast-progress-streaming.md) is
  // the only forecast path. It keeps Safari's HTTP/2 stream warm via emit_progress
  // + a 10s heartbeat instead of going silent for 90s and getting reset. It needs
  // a signed-in user and Postgres; without them it refuses and the run fails.
  //
  // It yields a sequence of SSE events; we collect traceAllocated headers + wait
  // for `done` then GET the persisted result. `progress` events are noisy at this
  // layer — submit() owns the user-facing stage label updates via a separate
  // task; this function just returns the final forecast.
  const t0 = Date.now();
  const recoveryDeadlineAt = resolveRecoveryDeadlineAt(
    t0,
    opts.recoveryDeadlineAt ?? t0 + FORECAST_RECOVERY_WINDOW_MS,
  );
  // `_obs.backend` mirrors the authoritative `run.backend` = the requested engine.
  // It is deliberately NOT the `x-forecast-backend` deploy label (a server-side
  // constant, "sooth-forecast"): letting that label leak into `run.backend` is what
  // made branch-create send `orchestrator:"sooth-forecast"` → backend 400 "unknown
  // orchestrator". The deploy label lives server-side only (experiment_arm telemetry).
  let traceId = null,
    forecastId = null,
    backend = engine,
    requestId = null,
    resultUrl = null,
    sawDone = false,
    errPayload = null,
    // The highest positioned typed `update` seq this stream delivered — what a
    // mid-stream recovery resumes from, so the replay covers exactly the gap (#5611).
    lastUpdateSeq = null;
  try {
    const { runForecastStream, typedSeqOf } = await import("../forecastStream.js");
    for await (const evt of runForecastStream(body, signal, {
      recoveryDeadlineAt,
      // The user intent this run belongs to (lib/telemetry/intentDeps.ts): the stream
      // request carries it as x-intent-id, and the run's end ends the intent.
      intentId: opts.intentId ?? null,
    })) {
      if (evt.type === "traceAllocated") {
        traceId = evt.traceId;
        forecastId = evt.forecastId;
        requestId = evt.requestId || null; // backend stays = engine (see above); the SSE frame's deploy label is intentionally ignored
        // Persist the single-slot reload-resume handle only for TOP-LEVEL
        // submits. Branches (assumption-injection) carry a clientId too now —
        // so the proxy keys their lifecycle row by a stable, client-known id
        // (enabling forecastId recovery on read) — but they must NOT write the
        // top-level PENDING_FORECAST_KEY handle: it's single-slot (would clobber
        // the real top-level forecast) and on reload would resurrect the branch
        // as an independent top-level forecast with the branch's question text.
        // Branches resume from their persisted server row instead (the resume
        // sweep's kind:'branch' path → reattachBranch). So gate on `!assumption`
        // (= not a branch), decoupling the resume-handle write from the
        // clientId/dedup role they previously shared. Cleared on any terminal
        // exit (success, error, cancel).
        if (clientId && !pairLeg && !assumption) {
          try {
            sessionStorage.setItem(
              PENDING_FORECAST_KEY,
              JSON.stringify({
                // clientId is the wire-side id (forecasts.client_id),
                // shared between the optimistic placeholder, the proxy
                // lifecycle row, and the reattach placeholder. Without it,
                // reattach would create a duplicate row keyed by the UUID
                // forecastId.
                traceId,
                forecastId,
                clientId,
                question: q,
                engine,
                // Carry the preprocessed question so a page reload can render
                // the contract panel (resolution date / criteria) immediately
                // — before the forecasts[] list reloads — and re-surface the
                // preprocess confirmation instead of silently skipping it. The
                // server persists the same contract on the lifecycle row, but
                // the handle gives the resume an instant, list-load-free path.
                preprocessed: preprocessedQuestion || null,
                // The panel's question-type routing, so a SUBMIT EDIT after the reload
                // re-authors the question the way the submit did (commands/editResubmit).
                ...(opts.routing ? { routing: opts.routing } : {}),
                submittedAt: Date.now(),
                recoveryDeadlineAt,
              }),
            );
          } catch (_storageErr) {
            /* tab in private mode / quota — skip */
          }
        }
        // Notify caller so it can populate React state (Cancel button,
        // active-trace tracking, messages-persistence forecast id).
        if (onTraceAllocated)
          try {
            onTraceAllocated({ traceId, forecastId, backend });
          } catch (_cbErr) {
            /* ignore */
          }
      } else if (evt.type === "done") {
        sawDone = true;
        resultUrl = evt.data?.result_url;
      } else if (evt.type === "error") {
        errPayload = evt.data || {};
        break;
      } else if (evt.type === "cancelled") {
        throw Object.assign(new Error("cancelled"), { name: "AbortError" });
      } else if (evt.type === "update") {
        // The typed `sooth-idl` progress vocabulary. Unlike `progress`, these
        // are not interpreted here — the caller decides what to do with them,
        // which today is the flag-gated debug block in the chat console.
        // Wrapped like the sibling callbacks: a UI hiccup must not drop a
        // forecast that is otherwise fine. The cursor acknowledges a frame only
        // when the callback took it (or nobody wanted it): a frame the UI failed
        // to fold is one a recovery may still replay if it was the last before
        // the cut — the cursor is a high-water mark, so a later folded frame
        // moves past it regardless.
        let delivered = true;
        if (onUpdate) {
          try {
            onUpdate(evt.data || {});
          } catch (_cbErr) {
            delivered = false; /* swallow — UI hiccup shouldn't drop forecast */
          }
        }
        const seq = typedSeqOf(evt.data?.seq);
        if (delivered && seq !== null && (lastUpdateSeq === null || seq > lastUpdateSeq)) {
          lastUpdateSeq = seq;
        }
      } else if (evt.type === "narration" && onNarration) {
        // Commentary, not contract: a dropped beat costs one chat line, so
        // unlike `update` this never touches the typed resume cursor.
        try {
          onNarration(evt.data || {});
        } catch (_cbErr) {
          /* swallow — a missing narration line is not a broken forecast */
        }
      } else if (evt.type === "progress" && onProgress) {
        try {
          onProgress(evt.data || {});
        } catch (_cbErr) {
          /* swallow — UI hiccup shouldn't drop forecast */
        }
      } else if (evt.type === "contract_key") {
        // Server-allocated contract key (User#<hash>), emitted early — before the
        // upstream forecast opens. Capture for _obs (debug display) and notify the
        // caller so it lands in the client log ring buffer. Best-effort callback.
        const d = evt.data || {};
        contractKey = d.key || null;
        contractKeyStatus = d.key_status || null;
        if (onContractKey)
          try {
            onContractKey({ key: contractKey, key_status: contractKeyStatus });
          } catch (_cbErr) {
            /* telemetry best-effort */
          }
      }
      // heartbeat events keep the connection warm; nothing to render.
    }
    // A proxy timeout can close the body cleanly without a terminal frame.
    // Recover before clearing the handle, just as for a failed stream read.
    if (!sawDone && !errPayload) throw new Error("forecast stream ended without done event");
    // Error/malformed terminals clear the handle now; valid done waits for its
    // bounded result read below. We *don't* clear in finally because
    // page reload aborts the fetch and would synchronously fire finally
    // before navigation completes, wiping the handle the new tab needs to
    // reattach. AbortError + network failure paths keep the handle so the
    // mount effect on the next load can pick up where this run left off.
    // For a compare leg, clear ONLY this leg's entry in the dual-leg store
    // (keyed by traceId) so the other leg's resume handle survives.
    if (pairLeg && (!resultUrl || errPayload)) {
      if (traceId)
        try {
          const { removeComparePendingLeg } = await import("../comparePending.js");
          removeComparePendingLeg(traceId);
        } catch (_e) {
          /* ignore */
        }
    } else if (!resultUrl || errPayload) clearPendingForecastIfOwned(traceId, clientId);
  } catch (streamErr) {
    if (streamErr.name === "AbortError") throw streamErr;
    if (!traceId) {
      // Pre-traceAllocated failure — no trace key to query or resume.
      // Surface the error so submit()'s caller can wake the server.
      const obs = { traceId: null, backend: engine, requestId, latencyMs: Date.now() - t0 };
      const e = new Error(streamErr.message || "forecast stream failed");
      e._obs = obs;
      throw e;
    }
    // Transport interruption mid-stream (clean EOF, fetch drop, instance recycle).
    // tryRecoverMidStream polls /result for up to 30s — Safari drops near
    // the end of a forecast often happen after the result is persisted in
    // GCS, in which case this returns in 1-2 ticks. If still running we
    // fall back to resumeForecastStream + /result.
    try {
      const { tryRecoverMidStream } = await import("../forecastStream.js");
      data = await tryRecoverMidStream(traceId, signal, {
        onProgress,
        onUpdate,
        // Recovery re-reads the stream, so it re-delivers every per-frame
        // callback. Omitting one here makes that callback's branch inside
        // `tryRecoverMidStream` dead code on the recovery path.
        onNarration,
        sinceSeq: lastUpdateSeq,
        recoveryDeadlineAt,
        recoverySleep: opts.recoverySleep,
        resumeRetryDelayMs: opts.resumeRetryDelayMs,
        now: opts.recoveryNow,
      });
    } catch (recoverErr) {
      if (recoverErr.name === "AbortError") throw recoverErr;
      if (recoverErr.terminal) {
        if (pairLeg) {
          if (traceId)
            try {
              const { removeComparePendingLeg } = await import("../comparePending.js");
              removeComparePendingLeg(traceId);
            } catch (_e) {
              /* ignore */
            }
        } else clearPendingForecastIfOwned(traceId, clientId);
      }
      const obs = { traceId, backend, requestId, latencyMs: Date.now() - t0 };
      const e = new Error(recoverErr.message || "forecast recovery failed");
      e._obs = obs;
      // The command layer distinguishes absolute wait exhaustion from a
      // terminal forecast failure. Preserve this narrow classification when
      // adding client observability instead of flattening it into a generic Error.
      if (recoverErr.code) e.code = recoverErr.code;
      if (!recoverErr.terminal) e._preservePendingHandle = true;
      throw e;
    }
    // Mid-stream recovery succeeded — same per-leg vs single-key cleanup split.
    if (pairLeg) {
      if (traceId)
        try {
          const { removeComparePendingLeg } = await import("../comparePending.js");
          removeComparePendingLeg(traceId);
        } catch (_e) {
          /* ignore */
        }
    } else clearPendingForecastIfOwned(traceId, clientId);
    const obs = { traceId, forecastId, backend, requestId, latencyMs: Date.now() - t0 };
    data._obs = obs;
  }
  if (!data && errPayload) {
    const obs = { traceId, backend, requestId, latencyMs: Date.now() - t0 };
    const e = new Error(errPayload.message || errPayload.code || "forecast error");
    if (errPayload.code) e.code = errPayload.code;
    e._obs = obs;
    throw e;
  }
  if (!data) {
    if (!resultUrl) throw new Error("forecast stream ended without done event");
    // Fetch the GCS-persisted result. Chatboard's /api/forecasts/:trace_id/result
    // is the user-facing proxy (the SSE done event carries the forecaster-side
    // URL /forecast/result/<trace>, but chatboard's route is the auth-gated entry).
    const obs = {
      traceId,
      forecastId,
      backend,
      requestId,
      latencyMs: Date.now() - t0,
    };
    let rr;
    try {
      rr = await boundedResultInspection(
        traceId,
        signal,
        async (_trace, { signal: readSignal }) => {
          const response = await fetchForecastResult(traceId, { signal: readSignal });
          return {
            ok: response.ok,
            status: response.status,
            result: response.ok ? await response.json() : await parseJsonBodyOrNull(response),
          };
        },
        { recoveryDeadlineAt },
      );
    } catch (error) {
      error._obs = obs;
      // A stalled or dropped read after `done` says nothing about the result, which is
      // durable by now: keep the handle so the run can be reattached and read again.
      if (
        isRecoveryWaitExpiredError(error) ||
        error.name === "AbortError" ||
        isUnansweredRecoveryError(error)
      )
        error._preservePendingHandle = true;
      throw error;
    }
    if (!rr.ok && rr.status === 404) {
      throw Object.assign(new Error("Forecast result is not available yet"), {
        code: "forecast_result_pending",
        _preservePendingHandle: true,
        _obs: obs,
      });
    }
    if (pairLeg) {
      if (traceId) {
        const { removeComparePendingLeg } = await import("../comparePending.js");
        removeComparePendingLeg(traceId);
      }
    } else clearPendingForecastIfOwned(traceId, clientId);
    if (!rr.ok) {
      const dat = rr.result || {};
      const e = new Error(dat.error || dat.detail || `Result fetch returned ${rr.status}`);
      e._obs = obs;
      throw e;
    }
    data = rr.result;
    data._obs = obs;
  }

  // Carry the contract key into _obs so the forecast log can surface it
  // (debug-only). Placed after recovery so it applies to the FINAL `data`.
  if (contractKey && data && data._obs) {
    data._obs.contractKey = contractKey;
    data._obs.contractKeyStatus = contractKeyStatus;
  }
  // Carry the server-echoed effective trigger LLM into _obs for the debug panel.
  // Stamp the effective trigger model onto _obs ONLY when the server reported
  // it (it may legitimately be null = default). Absent (older servers / tour
  // paths) → leave it unset so the debug panel hides the line rather than
  // claiming "Server default" for an unknown model.
  if (data && data._obs && data.postprocess_model !== undefined)
    data._obs.postprocessModel = data.postprocess_model;
  // Mark the forecast as partial when the final response is still missing events —
  // the caller surfaces this to the user instead of silently rendering a placeholder.
  const _partial = isIncomplete(data);

  // The wire's `contract.context` is the record's `background`: mapped here, where the
  // contract enters the record, so every reader sees one key.
  if (data.contract) data.contract = withBackground(data.contract);
  // Accept any response that has a contract — events list is allowed to be
  // empty (LLM postprocess sometimes returns no events on questions with
  // sparse research artifacts; the contract + probability is still useful).
  if (data.contract) {
    const events = Array.isArray(data.events) ? filterAndStampEvents(data.events) : [];
    // Defensive title backfill — the orchestrator occasionally ships trigger events
    // with empty/null `title` fields (labelling step failed or returned malformed
    // JSON downstream of the extraction step). Without this, the trigger glyph
    // renders on the timeline but the label text is empty → user sees N circles
    // with no captions and no error message. Backfill with a numbered placeholder
    // so the timeline label is at least readable, and console.warn so we can spot
    // the upstream issue from the browser console / bug bundles.
    let _missingTitles = 0;
    const _retitled = new Set();
    events.forEach((e, i) => {
      if (!e.title || !String(e.title).trim()) {
        _missingTitles++;
        e.title = `Trigger ${i + 1}`;
        _retitled.add(e);
      }
    });
    if (_missingTitles > 0) {
      // filterAndStampEvents above already stamped `occurrenceKey`, and it did so from
      // the EMPTY title — so a backfilled event is carrying the unkeyable `occ-id:<id>`
      // fallback. Left alone it would keep that key while its title now reads
      // "Trigger N", and the next vintage would derive a title-based key instead: a
      // different key for the same occurrence, which cannot reconcile its live node at
      // `done` and would be admitted twice by the accumulate-past dedup. Drop the
      // provisional key on exactly the retitled events and re-stamp from the final
      // titles. (stampOccurrenceKeys is idempotent, so it will not touch the others.)
      for (const e of _retitled) delete e.occurrenceKey;
      stampOccurrenceKeys(events);
      console.warn(
        `[forecast] ${_missingTitles}/${events.length} trigger events arrived without a title (orchestrator=${orchestrator}); backfilled with placeholders`,
      );
    }
    // Defensive resolution_criteria backfill — same class of partial/malformed
    // contract as the missing-title case above. The orchestrator occasionally
    // returns a contract without resolution_criteria, so the contract drawer's
    // RESOLUTION field renders empty ("—") even though the user defined criteria
    // at preprocess time. Restore it from the preprocessed question the user
    // already saw and approved, so the resolution they defined persists.
    // Only backfill when the preprocessed criteria has non-whitespace content —
    // mirror the missing-check's trim() so a blank/whitespace preprocess value
    // doesn't overwrite with an equally-empty string and leave the drawer blank.
    if (
      (!data.contract.resolution_criteria || !String(data.contract.resolution_criteria).trim()) &&
      preprocessedQuestion?.resolution_criteria &&
      String(preprocessedQuestion.resolution_criteria).trim()
    ) {
      data.contract.resolution_criteria = preprocessedQuestion.resolution_criteria;
      console.warn(
        `[forecast] server contract missing resolution_criteria (orchestrator=${orchestrator}); backfilled from preprocessed question`,
      );
    }
    return {
      id: "fc-" + uid(),
      question: q,
      createdAt: new Date().toISOString(),
      branches: [],
      serverPrediction:
        typeof data.contract.probability === "number"
          ? data.contract.probability
          : parseFloat(data.contract.probability) || 0,
      serverExplanation: data.explanation || "",
      contract: data.contract,
      events,
      report: data.report || null,
      serverContext: {
        explanation: data.explanation || "",
        metadata: { ...(data.metadata || {}), orchestrator: engine },
        question: data.question || {},
        report: data.report || null,
      },
      _debug: data._debug || null,
      _obs: data._obs || null,
      _partial,
      contractKey: contractKey,
      contractKeyStatus: contractKeyStatus,
      ...premiseOfResult(data),
    };
  }

  // Fallback: construct minimal response from whatever the server returned
  const prob =
    data.contract?.probability || (typeof data.prediction === "number" ? data.prediction : null);
  if (prob != null) {
    const p =
      typeof prob === "number" ? Math.max(0.001, Math.min(0.999, prob)) : parseFloat(prob) || 0.5;
    const explanation = data.explanation || "";
    const today = new Date().toISOString().slice(0, 10);
    // The no-contract fallback, and it reads the deadline the SAME way every other
    // reader does. `new Date(scheduled_resolution_time)` parsed the naive-UTC wire
    // value in the BROWSER's zone — the original nine-hour bug — and then sliced
    // UTC's day off it, so this path could hand back a different calendar day than
    // the contract path for the identical question.
    const fallbackDeadline = deadlineOf(data.question);
    const fallbackExact = exactDeadlineOf(data.question);
    const resDate = fallbackExact
      ? dayInZone(fallbackExact, data.question?.custom_metadata?.resolution_zone || "")
      : fallbackDeadline
        ? fallbackDeadline.toISOString().slice(0, 10)
        : data.contract?.resolution_date ||
          new Date(Date.now() + 180 * 86400000).toISOString().slice(0, 10);
    const contract = data.contract || {
      title: q.slice(0, 60),
      question: q,
      background: explanation.slice(0, 500),
      resolution_date: resDate,
      resolution_criteria: "Sayer forecast",
      sources: ["Sayer"],
      probability: p,
    };
    if (!contract.probability) contract.probability = p;
    const events =
      Array.isArray(data.events) && data.events.length > 0
        ? data.events
        : [
            {
              id: "e-srv",
              date: today,
              title: "Forecast made",
              description: `Sayer forecast a ${sideChanceText(p)}. ${explanation.slice(0, 200)}`,
              p_event: 1.0,
              p_conditional: p >= 0.5 ? p : 1 - p,
              isPast: true,
              source: "Sayer",
              direction: p >= 0.5 ? "yes" : "no",
            },
          ];
    const now = new Date();
    events.forEach((e) => {
      if (!e.date) return;
      // Local-day rule, as above.
      const dMs = eventEndMs(e.date);
      e.isPast = dMs <= now.getTime();
    });
    return {
      id: "fc-" + uid(),
      question: q,
      createdAt: new Date().toISOString(),
      branches: [],
      serverPrediction: p,
      serverExplanation: explanation,
      contract,
      events,
      report: data.report || null,
      serverContext: {
        explanation,
        metadata: { ...(data.metadata || {}), orchestrator: engine },
        question: data.question || {},
        report: data.report || null,
      },
      _debug: data._debug || null,
      _obs: data._obs || null,
      _partial: true,
      ...premiseOfResult(data),
    };
  }
  // Truly empty — no contract, no prediction. Can't render anything.
  throw new Error("Sayer couldn't finish this forecast. Ask your question again to retry.");
}

// streamBranch — the SINGLE branch-creation SSE path. Owns the
// assumption-injected genForecastFromServer call + its fcOpts wiring
// (parentForecastId / clientId / onProgress / onUpdate / onTraceAllocated) and the
// shared `evts`/`isPast` post-processing. It is called by createBranch (the
// unified exec path, in-component) and by the reforecast rerun loop, so no second
// branch SSE path exists. Returns the post-processed result plus the trace-stamped
// forecastId.
//
// @param {object}   question        Parent question payload (genForecastFromServer's `q`)
// @param {string}   engine          Resolved forecaster name
// @param {string}   assumption      Server-facing assumption string
// @param {AbortSignal|null} signal  Abort signal
// @param {object}   parentContext   { parentForecastId, clientId, onProgress, onUpdate, onContractKey, onTraceAllocated, tourFixture, tourBranchIncrease }
// @returns {Promise<{contract, events, serverContext, serverExplanation, report, stampedForecastId, _obs, premiseKey, premiseContract}>}
export async function streamBranch(question, engine, assumption, signal, parentContext = {}) {
  // Capture the branch's server UUID via traceAllocated so callers can stamp
  // it on the returned branch — sub-branches submitted later pass this as
  // their parent_forecast_id.
  let stampedForecastId = null;
  const userTraceCb = parentContext.onTraceAllocated;
  const fcOpts = {
    parentForecastId: parentContext.parentForecastId || null,
    // Multi-value ("multiway") branch: the parent's outcome space, so genForecastFromServer
    // targets the distribution lane + sends body.outcome_space (null for a binary parent).
    outcomeSpace: parentContext.outcomeSpace || null,
    // The branch's own stable client id (= its placeholder runId). Keys the
    // proxy lifecycle row by a client-known id so the server read-path can
    // recover this branch's forecastId by joining branch.id = child client_id
    // — no longer dependent on the save winning the race against the trace
    // stamp. genForecastFromServer gates the top-level resume handle on
    // `!assumption`, so passing clientId here does NOT write that handle.
    clientId: parentContext.clientId || null,
    // A rerun of an existing branch names its row: the proxy otherwise looks the row up by
    // (caller, clientId), which misses a branch another collaborator created and inserts a
    // duplicate. Null on create.
    forecastId: parentContext.forecastId || null,
    // Fork metadata — threaded through to genForecastFromServer → request body
    // so the server persists a non-null fork column on branch-create. Without
    // this, newly-created branches reload with fork=null and render detached.
    forkDate: parentContext.forkDate ?? null,
    modification: parentContext.modification ?? null,
    forkEvent: parentContext.forkEvent ?? null,
    // Branch soft-context (parent p0 + report reasons + clicked trigger a/b) →
    // request body `branch_context`; the server injects it as the prior for the
    // branch re-forecast. Null for callers that don't supply it.
    branchContext: parentContext.branchContext ?? null,
    // Explicit branch sense → request body `branch_sense` (sturdier than the server
    // sniffing it from the assumption text). Null for callers that don't supply it.
    branchSense: parentContext.branchSense ?? null,
    // Per-leg trigger-LLM override, inherited from the branch's lane and forwarded
    // to the forecast request body (genForecastFromServer reads opts.postprocessModel).
    postprocessModel: parentContext.postprocessModel ?? null,
    // Forward branch progress so genForecastFromServer's SSE loop surfaces it
    // (the branch placeholder updater lives in the caller's parentContext).
    onProgress: parentContext.onProgress,
    onUpdate: parentContext.onUpdate,
    // Forwarded HERE rather than at each call site: branchForecast and the
    // reforecast branch path both reach the wire through this one function, so
    // a binding they pass is a no-op unless it is threaded through `fcOpts`.
    onNarration: parentContext.onNarration,
    onContractKey: parentContext.onContractKey,
    onTraceAllocated: ({ traceId, forecastId, backend }) => {
      stampedForecastId = forecastId;
      if (userTraceCb) {
        try {
          userTraceCb({ traceId, forecastId, backend });
        } catch (_e) {
          /* ignore */
        }
      }
    },
    // The branch's user intent (lib/telemetry/intentDeps.ts merges it into parentContext).
    intentId: parentContext.intentId ?? null,
    tourFixture: parentContext.tourFixture,
    tourBranchIncrease: parentContext.tourBranchIncrease,
  };
  // The parent's contract rebuilt as a preprocessed question (branchForecast supplies it), so
  // the branch streams the root's criteria/background/date rather than a bare string. Null
  // for callers without a parent contract, which keeps the legacy bare-question body.
  const preprocessed = parentContext.preprocessedQuestion ?? null;
  const fc = await genForecastFromServer(
    question,
    engine,
    assumption,
    false,
    preprocessed,
    signal,
    fcOpts,
  );
  const events = filterAndStampEvents(fc.events);
  return {
    contract: fc.contract || null,
    events,
    serverContext: fc.serverContext || {},
    serverExplanation: fc.serverExplanation || "",
    report: fc.report || null,
    stampedForecastId,
    _obs: fc._obs || null,
    premiseKey: fc.premiseKey || null,
    premiseContract: fc.premiseContract || null,
  };
}

export async function estimateSeriesImpacts(description, date, seriesNames, signal = null) {
  // Coerce common LLM outputs ("−25%", "-25.0", "$25", "~30") to a number.
  // Stripping non-numeric chars (other than the leading sign and a decimal point) catches the
  // overwhelming majority of strings the model returns when it ignores "numbers only".
  // Spelled-out values ("thirty-ish") are NOT supported — they return null.
  const coerce = (v) => {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v !== "string") return null;
    // Normalize unicode minuses, drop thousands-separator commas BEFORE the
    // non-numeric strip (otherwise "1,200" → "1 200" → first token "1" → 1
    // instead of 1200), strip remaining non-numeric junk, re-glue a sign to
    // the following digit group (handles "− 25%" / "- 25" where whitespace
    // between the sign and the digits would otherwise leave just "-" as the
    // first split token, which parseFloat treats as NaN).
    const cleaned = v
      .replace(/[−–—]/g, "-")
      .replace(/,/g, "")
      .replace(/[^\d.+-]/g, " ")
      .replace(/([+-])\s+(?=\d)/g, "$1")
      .trim()
      .split(/\s+/)[0];
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : null;
  };
  try {
    const prompt = `A forecasting event occurs on ${date}: "${description}".
Estimate the percentage price/value change this event would cause for each index below.
The event MAY be directly about one of these indices (e.g. an acquisition or bankruptcy involving the issuer) — in that case the impact should reflect the magnitude of the event, not a default near-zero.
Reply with ONLY a JSON object mapping index name to a numeric percent change (positive = increase, negative = decrease). Values MUST be plain JSON numbers — no % sign, no quotes, no units. No prose, no markdown fences.
Indices: ${seriesNames.join(", ")}
Example: {"S&P 500": -2.5, "10Y Treasury": 0.15}`;
    // Through callLlm: it bounds the call with the same timeout as every other generic
    // prompt and throws on a non-2xx, so an upstream failure is reported as one instead of
    // reading an error body as an empty answer.
    let text;
    try {
      text = await callLlm(prompt, { signal });
    } catch (e) {
      if (signal?.aborted) return {};
      console.warn("[seriesImpacts] /llm call failed:", e?.message || String(e));
      return {};
    }
    // Greedy match from the first `{` to the last `}` — handles nested objects and
    // prose-then-JSON wrappers that the previous `[^}]+` regex would truncate.
    const first = text.indexOf("{"),
      last = text.lastIndexOf("}");
    if (first < 0 || last <= first) {
      console.warn("[seriesImpacts] no JSON in response:", text.slice(0, 200));
      return {};
    }
    let parsed;
    try {
      parsed = JSON.parse(text.slice(first, last + 1));
    } catch (_e) {
      console.warn("[seriesImpacts] JSON parse failed:", text.slice(first, last + 1).slice(0, 200));
      return {};
    }
    const impacts = {};
    // Normalize each LLM-returned key back to the requested series name. The LLM
    // often rephrases ("S&P 500" → "S&P 500 Index", "10Y Treasury" → "10-Year
    // Treasury"), and a strict-equality lookup would silently drop the impact.
    for (const [name, raw] of Object.entries(parsed)) {
      const delta = coerce(raw);
      if (delta === null) continue;
      const nameLower = String(name).toLowerCase().trim();
      let matched = seriesNames.find((s) => s.toLowerCase() === nameLower);
      if (!matched)
        matched = seriesNames.find((s) => {
          const sl = s.toLowerCase();
          return nameLower.includes(sl) || sl.includes(nameLower);
        });
      // When only one index was requested, accept whatever single key the LLM returns.
      if (!matched && seriesNames.length === 1) matched = seriesNames[0];
      if (matched) impacts[matched] = [{ date, delta_percent: delta }];
    }
    // Warn loudly when the LLM gave us back ZERO usable impacts despite being asked —
    // makes "branch dashed line is missing" debuggable from the console.
    const missing = seriesNames.filter((s) => !impacts[s]);
    if (missing.length)
      console.warn("[seriesImpacts] no impact returned for:", missing, "raw:", text.slice(0, 200));
    return impacts;
  } catch (e) {
    console.warn("Series impact estimation failed:", e);
    return {};
  }
}
